import { createEntityAdapter, createSlice, EntityState } from "@reduxjs/toolkit";

export type PopInSessionEvent = {
	id: string;
	liveSessionId: string;
	type: string;
	timestamp: string;
	payload?: {
		flagLevel?: number;
	};
};

type Role = "proctor" | "student";

export type PopInSessionUserSession = {
	id: string;
	userId: string;
	liveSessionId: string;
	role: Role;
	lastPingTime?: number;
};

export type PopInSession = {
	id: string;
	instituteId: string;
	courseId: string;
	assessmentId: string;
	startTime: string;
	users: { userId: string; role: Role }[];
	userSessions: EntityState<PopInSessionUserSession, string>;
	studentId?: string;
	studentUserSessionId?: string;
	events: EntityState<PopInSessionEvent, string>;
	lastViewedTimestamp?: number;
};

export type PopInSessionPayload = Omit<
	PopInSession,
	"studentUuid" | "studentName" | "userSessions" | "events" | "lastViewedTimestamp" | "users"
> & {
	events: Record<string, PopInSessionEvent>;
	userSessions: Record<string, PopInSessionUserSession>;
	lastViewedTimestamp: Record<string, number>;
	users: { id: string; userId: string; role: Role }[];
};

export type PopInSessionUpdate = Pick<PopInSessionPayload, "id" | "lastViewedTimestamp">;

export type PopInSessionsSliceState = {
	sessions: EntityState<PopInSession, string>;
	isLoading: boolean;
	isError: boolean;
	fulfilledTimeStamp?: number;
};

const popInSessionsAdapter = createEntityAdapter<PopInSession>();
export const popInSessionUserSessionsAdapter = createEntityAdapter<PopInSessionUserSession>();
export const popInSessionEventsAdapter = createEntityAdapter<PopInSessionEvent>({
	sortComparer: (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
});

const initialState: PopInSessionsSliceState = {
	sessions: popInSessionsAdapter.getInitialState(),
	isLoading: true,
	isError: false,
};

const popInSessionsSlice = createSlice({
	name: "popInSessions",
	initialState,
	reducers: {
		popInSessionStarted: (state, { payload }: { payload: PopInSession }) => {
			state.sessions = popInSessionsAdapter.addOne(state.sessions, payload);
		},
		popInSessionUpdated: (
			state,
			{
				payload,
			}: {
				payload: Pick<PopInSession, "id" | "lastViewedTimestamp">;
			},
		) => {
			const { id, lastViewedTimestamp } = payload;
			const prevLastViewedTimestamp = state.sessions.entities[id]?.lastViewedTimestamp;
			if (lastViewedTimestamp && (!prevLastViewedTimestamp || lastViewedTimestamp > prevLastViewedTimestamp)) {
				state.sessions = popInSessionsAdapter.updateOne(state.sessions, { id, changes: { lastViewedTimestamp } });
			}
		},
		popInSessionEnded: (state, { payload: sessionId }: { payload: string }) => {
			state.sessions = popInSessionsAdapter.removeOne(state.sessions, sessionId);
		},
		popInSessionUserJoined: (
			state,
			{
				payload,
			}: {
				payload: PopInSessionUserSession;
			},
		) => {
			const session = state.sessions.entities[payload.liveSessionId];
			if (session) {
				if (payload.role === "proctor") {
					session.userSessions = popInSessionUserSessionsAdapter.addOne(session.userSessions, payload);
				} else {
					session.studentUserSessionId = payload.id;
					session.studentId = payload.userId;
				}
			}
		},
		popInSessionUserLeft: (
			state,
			{
				payload,
			}: {
				payload: PopInSessionUserSession;
			},
		) => {
			const session = state.sessions.entities[payload.liveSessionId];
			if (session) {
				session.userSessions = popInSessionUserSessionsAdapter.removeOne(session.userSessions, payload.id);
			}
		},
		popInSessionEventCreated: (state, { payload }: { payload: PopInSessionEvent }) => {
			const session = state.sessions.entities[payload.liveSessionId];
			if (session && payload.payload?.flagLevel) {
				session.events = popInSessionEventsAdapter.addOne(session.events, payload);
			}
		},
		popInSessionEventUpdated: (state, { payload }: { payload: PopInSessionEvent }) => {
			const session = state.sessions.entities[payload.liveSessionId];
			if (session) {
				if (payload.payload?.flagLevel) {
					session.events = popInSessionEventsAdapter.setOne(session.events, payload);
				} else {
					session.events = popInSessionEventsAdapter.removeOne(session.events, payload.id);
				}
			}
		},
		fullStateUpdated: (
			_,
			{
				payload,
			}: {
				payload: PopInSession[];
			},
		) => ({
			sessions: popInSessionsAdapter.setAll(popInSessionsAdapter.getInitialState(), payload),
			isLoading: false,
			isError: false,
			fulfilledTimeStamp: Date.now(),
		}),
		resetState: () => initialState,
		errorLoading: (state) => {
			state.isError = true;
			state.isLoading = false;
		},
	},
});

export default popInSessionsSlice;

const selectPopInSession = (state: PopInSessionsSliceState, sessionId: string) => state.sessions.entities[sessionId];

export function getPopInSessionFlagCounts(session: PopInSession) {
	const flagCounts: [number, number, number] = [0, 0, 0];
	session.events.ids.forEach((eventId) => {
		const flagLevel = session.events.entities[eventId]?.payload?.flagLevel;
		if (flagLevel && flagLevel > 0) {
			flagCounts[flagLevel - 1] += 1;
		}
	});
	const [minor, major, severe] = flagCounts;
	return {
		severe,
		major,
		minor,
	};
}

export const selectors = {
	selectPopInSession,
};
